<script setup lang="ts">
    import {
        AisRefinementList,
        AisClearRefinements,
        AisHierarchicalMenu,
        AisDynamicWidgets,
        AisCurrentRefinements,
        AisSortBy,
        AisInstantSearch,
        AisHits,
        AisStateResults,
        type InstantSearchMiddleWare,
    } from 'vue-instantsearch/vue3/es';
    import { connectPagination, connectSearchBox } from 'instantsearch.js/es/connectors';
    import { createInsightsMiddleware } from 'instantsearch.js/es/middlewares';

    interface AlgoliaSearchResultProductsProps {
        routing?: Record<string, unknown>;
        middlewares?: InstantSearchMiddleWare[];
        indexName: string;
        showNoResult?: boolean;
        initialUiState?: Record<string, unknown>;
        hideHierarchyMenu?: boolean;
        hideAgeFacet?: boolean;
        hideColorsFacet?: boolean;
        hideHeightFacet?: boolean;
        hideWheelsFacet?: boolean;
        hideSort?: boolean;
    }

    const props = withDefaults(defineProps<AlgoliaSearchResultProductsProps>(), {
        middlewares: () => [],
    });

    const appStore = useAppStore();
    const route = useRoute();
    const nuxtApp = useNuxtApp();

    const { metric } = storeToRefs(appStore);
    const { friendlyAge, s } = useLocales();
    const { t } = useI18n({
        useScope: 'local',
    });
    const { algoliaIndex, algoliaSearchClient } = useAlgolia();
    const { saveQueryID, getStoredQueryText, clearQueryID, setColorRefinements } = useAlgoliaStore();
    const { showBikefinderButton, bikefinderLocaleUrl } = useBikefinder();

    const isLg = ref(false);
    useMediaQuery([
        {
            query: defaultQueries.min.lg,
            ref: isLg,
        },
    ]);

    const showHierarchyMenu = ref<boolean>();
    const hierarchyMenu = (items: any) => {
        showHierarchyMenu.value = !props.hideHierarchyMenu && !!items.length;
        return items;
    };
    const showAgeFacet = ref<boolean>();
    const facetAge = (items: AisRefinementList[]) => {
        showAgeFacet.value = !props.hideAgeFacet && !!items.length;
        return items;
    };
    const showColorsFacet = ref<boolean>();
    const facetColors = (items: AisRefinementList[]) => {
        showColorsFacet.value = !props.hideColorsFacet && !!items.length;
        return items;
    };
    const showHeightFacet = ref<boolean>();
    const facetHeight = (items: AisRefinementList[]) => {
        showHeightFacet.value = !props.hideHeightFacet && !!items.length;
        return items;
    };
    const showWheelsFacet = ref<boolean>();
    const facetWheels = (items: AisRefinementList[]) => {
        showWheelsFacet.value = !props.hideWheelsFacet && !!items.length;
        return items;
    };

    const handleAgeRefinementListItem = (value: string) => {
        try {
            const numberValue = Number(value);
            return friendlyAge(numberValue);
        } catch (error) {
            return value;
        }
    };
    const handleColorRefinementListItem = (value: string) => value;
    const handleHeightRefinementListItem = (value: string) => {
        try {
            const numberValue = Number(value);
            // convert from cm to inches if needed
            const actualValue = metric.value ? numberValue : Math.floor(numberValue / 2.54);
            return s(actualValue, !metric.value);
        } catch (error) {
            return value;
        }
    };
    const handleWheelsRefinementListItem = (value: string) => {
        return `${value}″`;
    };
    const currentRefinementMap: Record<string, (value: string) => string> = {
        [ALGOLIA.AGE]: handleAgeRefinementListItem,
        [ALGOLIA.COLORS]: handleColorRefinementListItem,
        [ALGOLIA.HEIGHT]: handleHeightRefinementListItem,
        [ALGOLIA.WHEELS]: handleWheelsRefinementListItem,
    } as const;
    const handleCurrentRefinementValue = (type: string, value: string) => {
        if (currentRefinementMap[type]) return currentRefinementMap[type](value);
        return categoryTranslation(value);
    };
    const sortRefinementsNumeric: AisRefinementList['sortBy'] = (a, b) => {
        return Number(a.name) - Number(b.name);
    };
    const CATEGORY_ORDER: string[] = ['Bikes', 'Helmets', 'Accessories', 'Spareparts'];
    const categorySortFunction = (a: { name: string }, b: { name: string }) => {
        const indexA = CATEGORY_ORDER.includes(a.name) ? CATEGORY_ORDER.indexOf(a.name) : Infinity;
        const indexB = CATEGORY_ORDER.includes(b.name) ? CATEGORY_ORDER.indexOf(b.name) : Infinity;
        return indexA - indexB;
    };

    const currentSort = ref(route.query.sortBy?.toString() || '');
    const sortItems = [
        // The base index must be included in ais-sort-by items.
        // InstantSearch initializes with the base index (e.g. PROD_DE_DE),
        // and omitting it causes the warning: "[InstantSearch.js]: The index named ... is not listed in the items of sortBy."
        // Hidden from the sortBy dropdown UI.
        { label: '', value: algoliaIndex.indexName },
        { label: t('latest'), value: `${algoliaIndex.indexName}_newest` },
        { label: t('priceAscending'), value: `${algoliaIndex.indexName}_price_asc` },
        { label: t('priceDescending'), value: `${algoliaIndex.indexName}_price_desc` },
        { label: t('rating'), value: `${algoliaIndex.indexName}_rating` },
    ];

    const visibleSortItems = (items: { label: string; value: string }[]) => items.filter((item) => item.value !== algoliaIndex.indexName);

    const categoryIdLookup = new Map<string, string>();
    const gtmQueryPushed = ref<string | null>(null);
    const transformCategoryItems = (items: any[], meta?: any) => {
        items.forEach((item) => {
            item.product_category?.forEach(({ id, name }: { id: string; name: string }) => {
                categoryIdLookup.set(name, id);
            });
        });

        // Route updates before Algolia results resolve, so we compare against the results query to avoid pushing stale (previous search) data to GTM.
        const resultsQuery = meta?.results?.query ?? '';
        if (items.length && gtmQueryPushed.value !== resultsQuery) {
            nuxtApp.$gtm.ecommerceViewItemList('search_results', 'Search Results', items);
            gtmQueryPushed.value = resultsQuery;
        }

        return items;
    };
    const translateSafe = (translationKey: string, fallback: string) => {
        const translation = t(translationKey);
        return translation === translationKey ? fallback : translation;
    };
    const categoryTranslation = (category: string) => {
        return category
            .split(ALGOLIA.CATEGORY_SEPARATOR)
            .map((categoryPart) => {
                const categoryId = categoryIdLookup.get(categoryPart.trim());
                if (!categoryId) return categoryPart;
                return translateSafe(`categories.${categoryId}`, categoryPart);
            })
            .join(ALGOLIA.CATEGORY_SEPARATOR);
    };

    const virtualPagination = connectPagination(() => null);
    const virtualSearchBox = connectSearchBox(() => null);
    const baseMiddleware: InstantSearchMiddleWare = ({ instantSearchInstance }) => {
        instantSearchInstance.mainIndex.addWidgets([virtualPagination({}), virtualSearchBox({})]);
        return {
            onStateChange() {
                setColorRefinements(instantSearchInstance.mainIndex.getHelper()?.state.disjunctiveFacetsRefinements[ALGOLIA.COLORS] ?? []);
            },
        };
    };
    const insightsMiddleware = createInsightsMiddleware({
        insightsClient: null, // or `window.aa`
        onEvent(event) {
            const { widgetType, eventType, payload, hits } = event;

            if (widgetType === 'ais.hits' && eventType === 'view') {
                nuxtApp.$gtm.pushEvent({ event: 'Hits Viewed' });
            }

            const currentQuery = route.query.query?.toString() || '';
            const storedQueryText = getStoredQueryText();
            if (storedQueryText !== currentQuery) clearQueryID();

            const firstHit = hits?.[0];
            const newQueryID = firstHit?.__queryID;
            if (!newQueryID) return;
            saveQueryID(newQueryID, currentQuery);
        },
    });

    onMounted(() => {
        // Set up algolia user token
        if (window.aa) {
            window.aa('getUserToken', {}, (err: Error, algoliaUserToken: string) => {
                if (err) {
                    console.error(err);
                    return;
                }

                nuxtApp.$gtm.pushEvent({
                    event: 'set_algolia_user_token',
                    algoliaUserToken,
                });
            });
            window.aa(
                'onUserTokenChange',
                (algoliaUserToken: string) => {
                    nuxtApp.$gtm.pushEvent({
                        event: 'set_algolia_user_token',
                        algoliaUserToken,
                    });
                },
                { immediate: true },
            );
        }
    });
</script>
<template>
    <atm-container class="org-algolia-search-result-products py-8 md:py-10">
        <client-only>
            <ais-instant-search
                :data-insights-index="indexName"
                :future="{
                    preserveSharedStateOnUnmount: true,
                }"
                :index-name="indexName"
                :initial-ui-state="initialUiState"
                :middlewares="[baseMiddleware, insightsMiddleware, ...middlewares]"
                :search-client="algoliaSearchClient"
                :routing="routing">
                <slot></slot>
                <!-- Filters -->
                <!-- dynamic-widgets does not display hierarchical-menu, but refinement-list needs it to update results -->
                <div class="justify-between md:max-lg:flex">
                    <mol-algolia-filters-dialog>
                        <template v-if="!isLg">
                            <ais-current-refinements v-slot="{ items, createURL }">
                                <!-- padding for inside x padding on dialog -->
                                <div
                                    class="flex flex-col gap-4 px-6 pb-5"
                                    v-if="items.length">
                                    <atm-algolia-current-refinements
                                        :items="items"
                                        :category-name="t(ALGOLIA.CATEGORY)"
                                        :createURL="createURL"
                                        :handle-current-refinement-value="handleCurrentRefinementValue"
                                        :t="t" />
                                    <ais-clear-refinements>
                                        <template v-slot:resetLabel>{{ t('clearRefinement') }}</template>
                                    </ais-clear-refinements>
                                </div>
                            </ais-current-refinements>
                        </template>
                        <div class="lg:flex lg:gap-2">
                            <!-- AGE -->
                            <atm-algolia-facet-details
                                v-show="showAgeFacet"
                                v-if="!isLg"
                                open
                                :facet-name="t(ALGOLIA.AGE)">
                                <ais-dynamic-widgets :facets="['*']">
                                    <ais-refinement-list
                                        :attribute="ALGOLIA.AGE"
                                        :limit="ALGOLIA.REFINEMENT_LIMIT"
                                        :transform-items="facetAge"
                                        :sort-by="sortRefinementsNumeric">
                                        <template v-slot:item="{ item, refine, createURL }">
                                            <atm-algolia-refinement-list-item
                                                :attribute="ALGOLIA.AGE"
                                                :item="item"
                                                :refine="refine"
                                                :createURL="createURL"
                                                :get-value-with-postfix="handleAgeRefinementListItem" />
                                        </template>
                                    </ais-refinement-list>
                                </ais-dynamic-widgets>
                            </atm-algolia-facet-details>
                            <mol-algolia-facet-popover
                                v-show="showAgeFacet"
                                v-if="isLg"
                                :id="ALGOLIA.AGE"
                                :facet-name="t(ALGOLIA.AGE)">
                                <ais-dynamic-widgets :facets="['*']">
                                    <ais-refinement-list
                                        :attribute="ALGOLIA.AGE"
                                        :limit="ALGOLIA.REFINEMENT_LIMIT"
                                        :transform-items="facetAge"
                                        :sort-by="sortRefinementsNumeric">
                                        <template v-slot:item="{ item, refine, createURL }">
                                            <atm-algolia-refinement-list-item
                                                :attribute="ALGOLIA.AGE"
                                                :item="item"
                                                :refine="refine"
                                                :createURL="createURL"
                                                :get-value-with-postfix="handleAgeRefinementListItem" />
                                        </template>
                                    </ais-refinement-list>
                                </ais-dynamic-widgets>
                            </mol-algolia-facet-popover>
                            <!-- HEIGHT -->
                            <atm-algolia-facet-details
                                :get-value-with-postfix="handleColorRefinementListItem"
                                v-show="showHeightFacet"
                                v-if="!isLg"
                                :facet-name="t(ALGOLIA.HEIGHT)">
                                <ais-dynamic-widgets :facets="['*']">
                                    <ais-refinement-list
                                        :attribute="ALGOLIA.HEIGHT"
                                        :limit="ALGOLIA.REFINEMENT_LIMIT"
                                        :transform-items="facetHeight"
                                        :sort-by="sortRefinementsNumeric">
                                        <template v-slot:item="{ item, refine, createURL }">
                                            <atm-algolia-refinement-list-item
                                                :attribute="ALGOLIA.HEIGHT"
                                                :item="item"
                                                :refine="refine"
                                                :createURL="createURL"
                                                :get-value-with-postfix="handleHeightRefinementListItem" />
                                        </template>
                                    </ais-refinement-list>
                                </ais-dynamic-widgets>
                            </atm-algolia-facet-details>
                            <mol-algolia-facet-popover
                                v-show="showHeightFacet"
                                v-if="isLg"
                                :id="ALGOLIA.HEIGHT"
                                :facet-name="t(ALGOLIA.HEIGHT)">
                                <ais-dynamic-widgets :facets="['*']">
                                    <ais-refinement-list
                                        :attribute="ALGOLIA.HEIGHT"
                                        :limit="ALGOLIA.REFINEMENT_LIMIT"
                                        :transform-items="facetHeight"
                                        :sort-by="sortRefinementsNumeric">
                                        <template v-slot:item="{ item, refine, createURL }">
                                            <atm-algolia-refinement-list-item
                                                :attribute="ALGOLIA.HEIGHT"
                                                :item="item"
                                                :refine="refine"
                                                :createURL="createURL"
                                                :get-value-with-postfix="handleHeightRefinementListItem" />
                                        </template>
                                    </ais-refinement-list>
                                </ais-dynamic-widgets>
                            </mol-algolia-facet-popover>
                            <!-- CATEGORY -->
                            <atm-algolia-facet-details
                                v-show="showHierarchyMenu"
                                v-if="!isLg"
                                :facet-name="t(ALGOLIA.CATEGORY)"
                                open>
                                <ais-hierarchical-menu
                                    v-slot="{ items, refine, createURL }"
                                    :attributes="ALGOLIA.HIERARCHY_MENU_ITEMS"
                                    :sort-by="categorySortFunction"
                                    :transform-items="hierarchyMenu">
                                    <atm-algolia-hierarchical-menu-list
                                        :category-translation="categoryTranslation"
                                        :createURL="createURL"
                                        :items="items"
                                        :refine="refine"
                                        class="lg:px-3" />
                                </ais-hierarchical-menu>
                            </atm-algolia-facet-details>
                            <mol-algolia-facet-popover
                                v-show="showHierarchyMenu"
                                v-if="isLg"
                                :facet-name="t(ALGOLIA.CATEGORY)"
                                :id="ALGOLIA.CATEGORY">
                                <ais-hierarchical-menu
                                    v-slot="{ items, refine, createURL }"
                                    :attributes="ALGOLIA.HIERARCHY_MENU_ITEMS"
                                    :sort-by="categorySortFunction"
                                    :transform-items="hierarchyMenu">
                                    <atm-algolia-hierarchical-menu-list
                                        :category-translation="categoryTranslation"
                                        :createURL="createURL"
                                        :items="items"
                                        :refine="refine"
                                        class="lg:px-3" />
                                </ais-hierarchical-menu>
                            </mol-algolia-facet-popover>
                            <!-- WHEELS -->
                            <atm-algolia-facet-details
                                v-show="showWheelsFacet"
                                v-if="!isLg"
                                :facet-name="t(ALGOLIA.WHEELS)">
                                <ais-dynamic-widgets :facets="['*']">
                                    <ais-refinement-list
                                        :attribute="ALGOLIA.WHEELS"
                                        :limit="ALGOLIA.REFINEMENT_LIMIT"
                                        :transform-items="facetWheels"
                                        :sort-by="sortRefinementsNumeric">
                                        <template v-slot:item="{ item, refine, createURL }">
                                            <atm-algolia-refinement-list-item
                                                :attribute="ALGOLIA.WHEELS"
                                                :item="item"
                                                :refine="refine"
                                                :createURL="createURL"
                                                :get-value-with-postfix="handleWheelsRefinementListItem" />
                                        </template>
                                    </ais-refinement-list>
                                </ais-dynamic-widgets>
                            </atm-algolia-facet-details>
                            <mol-algolia-facet-popover
                                v-show="showWheelsFacet"
                                v-if="isLg"
                                :id="ALGOLIA.WHEELS"
                                :facet-name="t(ALGOLIA.WHEELS)">
                                <ais-dynamic-widgets :facets="['*']">
                                    <ais-refinement-list
                                        :attribute="ALGOLIA.WHEELS"
                                        :limit="ALGOLIA.REFINEMENT_LIMIT"
                                        :transform-items="facetWheels"
                                        :sort-by="sortRefinementsNumeric">
                                        <template v-slot:item="{ item, refine, createURL }">
                                            <atm-algolia-refinement-list-item
                                                :attribute="ALGOLIA.WHEELS"
                                                :item="item"
                                                :refine="refine"
                                                :createURL="createURL"
                                                :get-value-with-postfix="handleWheelsRefinementListItem" />
                                        </template>
                                    </ais-refinement-list>
                                </ais-dynamic-widgets>
                            </mol-algolia-facet-popover>
                            <!-- COLOR -->
                            <atm-algolia-facet-details
                                v-show="showColorsFacet"
                                v-if="!isLg"
                                :facet-name="t(ALGOLIA.COLORS)">
                                <ais-dynamic-widgets :facets="['*']">
                                    <ais-refinement-list
                                        :attribute="ALGOLIA.COLORS"
                                        :limit="ALGOLIA.REFINEMENT_LIMIT"
                                        :transform-items="facetColors">
                                        <template v-slot:item="{ item, refine, createURL }">
                                            <atm-algolia-refinement-list-item
                                                :attribute="ALGOLIA.COLORS"
                                                :item="item"
                                                :refine="refine"
                                                :createURL="createURL"
                                                :get-value-with-postfix="handleColorRefinementListItem"
                                                is-colors />
                                        </template>
                                    </ais-refinement-list>
                                </ais-dynamic-widgets>
                            </atm-algolia-facet-details>
                            <mol-algolia-facet-popover
                                v-show="showColorsFacet"
                                v-if="isLg"
                                :id="ALGOLIA.COLORS"
                                :facet-name="t(ALGOLIA.COLORS)">
                                <ais-dynamic-widgets :facets="['*']">
                                    <ais-refinement-list
                                        :attribute="ALGOLIA.COLORS"
                                        :limit="ALGOLIA.REFINEMENT_LIMIT"
                                        :transform-items="facetColors">
                                        <template v-slot:item="{ item, refine, createURL }">
                                            <atm-algolia-refinement-list-item
                                                :attribute="ALGOLIA.COLORS"
                                                :item="item"
                                                :refine="refine"
                                                :createURL="createURL"
                                                :get-value-with-postfix="handleColorRefinementListItem"
                                                is-colors />
                                        </template>
                                    </ais-refinement-list>
                                </ais-dynamic-widgets>
                            </mol-algolia-facet-popover>
                            <!-- SORT -->
                            <mol-algolia-facet-popover
                                v-if="isLg"
                                v-show="!hideSort"
                                class="lg:ml-auto"
                                :id="ALGOLIA.SORT"
                                :facet-name="t(ALGOLIA.SORT)"
                                :sort-items="sortItems"
                                v-model:current-sort="currentSort">
                                <ais-sort-by :items="sortItems">
                                    <template #default="{ items, refine, currentRefinement }">
                                        <lazy-atm-algolia-sort-list
                                            :items="visibleSortItems(items)"
                                            :refine="refine"
                                            :current-refinement="currentRefinement"
                                            v-model:current-sort="currentSort" />
                                    </template>
                                </ais-sort-by>
                            </mol-algolia-facet-popover>
                        </div>
                        <div
                            v-if="isLg"
                            class="flex flex-wrap gap-2 py-5">
                            <ais-current-refinements>
                                <template v-slot="{ items, createURL }">
                                    <atm-algolia-current-refinements
                                        :items="items"
                                        :category-name="t(ALGOLIA.CATEGORY)"
                                        :createURL="createURL"
                                        :handle-current-refinement-value="handleCurrentRefinementValue"
                                        :t="t" />
                                </template>
                            </ais-current-refinements>
                            <ais-clear-refinements>
                                <template v-slot:resetLabel>{{ t('clearRefinement') }}</template>
                            </ais-clear-refinements>
                        </div>
                    </mol-algolia-filters-dialog>
                    <mol-algolia-facet-popover
                        v-if="!isLg"
                        v-show="!hideSort"
                        :facet-name="t(ALGOLIA.SORT)"
                        :hide-on-mobile="false"
                        :id="ALGOLIA.SORT"
                        :sort-items="sortItems"
                        v-model:current-sort="currentSort"
                        class="max-lg:mb-8 max-lg:flex">
                        <ais-sort-by :items="sortItems">
                            <template #default="{ items, refine, currentRefinement }">
                                <lazy-atm-algolia-sort-list
                                    :items="visibleSortItems(items)"
                                    :refine="refine"
                                    :current-refinement="currentRefinement"
                                    v-model:current-sort="currentSort" />
                            </template>
                        </ais-sort-by>
                    </mol-algolia-facet-popover>
                </div>

                <ais-state-results v-slot="{ status, state, results }">
                    <atm-loader-circular
                        v-if="status === 'stalled'"
                        :class="['absolute left-1/2 -translate-x-1/2', { hidden: status !== 'stalled' }]" />

                    <ais-hits
                        v-if="results.hits.length"
                        class="text-woom-black!"
                        :transform-items="transformCategoryItems">
                        <template v-slot="{ items }">
                            <mol-search-result-products :items="items" />
                        </template>
                    </ais-hits>

                    <mol-algolia-no-results
                        v-else
                        :class="{ 'sr-only': !showNoResult }"
                        :query="state.query"
                        :no-result-heading="$t('algolia.noResultHeading')"
                        :no-result-message="$t('algolia.noResultMessage')"
                        :bikefinder-text="$t('bikefinder.label')"
                        :bikefinder-url="bikefinderLocaleUrl"
                        :show-bikefinder-button="showBikefinderButton" />
                </ais-state-results>
            </ais-instant-search>
        </client-only>
    </atm-container>
</template>
<style scoped>
    @reference "#main.css";
    /* hide default "no results" in ais-stats */
    :deep(.ais-Stats-text) {
        @apply hidden;
    }

    /* ais-refinement-list */
    :deep(.ais-RefinementList-item) {
        /* text-woom-black needed to override algolia styles */
        @apply flex flex-wrap py-3 text-woom-black lg:mx-3;
    }
    :deep(.ais-RefinementList-item:not(:last-child)) {
        @apply border-b border-woom-grey-mid;
    }

    /* ais-current-refinements */
    :deep(.ais-CurrentRefinements) {
        @apply mt-0;
    }
    :deep(.ais-CurrentRefinements),
    :deep(.ais-ClearRefinements) {
        /* inline flex to sit next to each other */
        @apply inline-flex;
    }

    /* ais-clear-refinements */
    :deep(.ais-ClearRefinements-button) {
        /* text-woom-black needed to override algolia styles */
        @apply bg-transparent text-woom-black underline underline-offset-4;
    }
    :deep(.ais-ClearRefinements-button:focus) {
        outline: revert;
    }
    :deep(.ais-ClearRefinements-button--disabled) {
        @apply hidden;
    }
</style>

<i18n lang="json">
{
    "da": {
        "age": "Alder",
        "categories": {
            "5637185826": "Alle produkter",
            "5637185870": "Allround",
            "5637185857": "Allroundcykler",
            "5637185850": "Tøj",
            "5637185836": "Taske",
            "5637185837": "Kurv",
            "5637185830": "Kurv tilbehør",
            "5637185838": "Klokke",
            "5637185839": "Cykeltasker",
            "5637185840": "Cykellygte",
            "5637185841": "Cykelpumper",
            "5637185874": "Bog",
            "5637185843": "Flaske",
            "5637185862": "Allround-elcykler",
            "5637185864": "El-mountainbikes",
            "5637185844": "Skærme",
            "5637185851": "Handsker",
            "5637185832": "Hjelm tilbehør",
            "5637185833": "Hjelm lys",
            "5637185845": "Støtteben",
            "5637185846": "Lås",
            "5637185866": "Mountainbikes",
            "5637185873": "Blandet",
            "5637185835": "Til cykler",
            "5637185849": "Til ryttere",
            "5637185876": "Emballage",
            "5637185852": "Beskyttere",
            "5637185847": "Cykelstativ",
            "5637185875": "Klistermærker",
            "5637185881": "Spareparts",
            "5637185854": "Plysdyr",
            "5637185883": "Legetøj",
            "5637185853": "Legetøjstilbehør",
            "5637185886": "Brugte cykler"
        },
        "category": "Produktkategori",
        "clearRefinement": "Slet alle filtre",
        "colors": "Farver",
        "height": "Højde",
        "latest": "Nyeste",
        "priceAscending": "Laveste pris",
        "priceDescending": "Højeste pris",
        "rating": "Bedst bedømte",
        "sort": "Sortér efter {option}",
        "wheels": "Cykelstørrelse"
    },
    "de": {
        "age": "Alter",
        "categories": {
            "5637185826": "Alle Produkte",
            "5637185870": "Allrounder",
            "5637185857": "Allround-Bikes",
            "5637185850": "Bekleidung",
            "5637185836": "Tasche",
            "5637185837": "Korb",
            "5637185830": "Korbzubehör",
            "5637185838": "Klingel",
            "5637185839": "Fahrradtasche",
            "5637185840": "Fahrradlicht",
            "5637185841": "Fahrradpumpe",
            "5637185874": "Büchlein",
            "5637185843": "Flasche",
            "5637185862": "E-Allround-Bikes",
            "5637185864": "E-Mountainbikes",
            "5637185844": "Schutzbleche",
            "5637185851": "Handschuhe",
            "5637185832": "Helmzubehör",
            "5637185833": "Helmlicht",
            "5637185845": "Seitenständer",
            "5637185846": "Schloss",
            "5637185866": "Mountainbikes",
            "5637185873": "Sonstiges",
            "5637185835": "Fürs Bike",
            "5637185849": "Fürs Kind",
            "5637185876": "Verpackung",
            "5637185852": "Schoner",
            "5637185847": "Gepäckträger",
            "5637185875": "Sticker",
            "5637185881": "Spareparts",
            "5637185854": "Stofftier",
            "5637185883": "Spielzeug",
            "5637185853": "Spielzeug-Zubehör",
            "5637185886": "Gebrauchträder"
        },
        "category": "Produktkategorie",
        "clearRefinement": "Alle Filter löschen",
        "colors": "Farben",
        "height": "Körpergröße",
        "latest": "Neueste",
        "priceAscending": "Preis aufsteigend",
        "priceDescending": "Preis absteigend",
        "rating": "Beste Bewertungen",
        "sort": "Sortieren nach {option}",
        "wheels": "Radgröße"
    },
    "en": {
        "age": "age",
        "categories": {
            "5637185826": "All",
            "5637185870": "Allround",
            "5637185857": "Allroundbikes",
            "5637185850": "Apparel",
            "5637185836": "Bag",
            "5637185837": "Basket",
            "5637185830": "Basket Accessories",
            "5637185838": "Bell",
            "5637185839": "Bike Bags",
            "5637185840": "Bike Light",
            "5637185841": "Bike Pumps",
            "5637185874": "Book",
            "5637185843": "Bottle",
            "5637185862": "E Allroundbikes",
            "5637185864": "E Mountainbikes",
            "5637185844": "Fenders",
            "5637185851": "Gloves",
            "5637185832": "Helmet Accessories",
            "5637185833": "Helmet Light",
            "5637185845": "Kickstand",
            "5637185846": "Lock",
            "5637185866": "Mountainbikes",
            "5637185873": "Miscellaneous",
            "5637185835": "On the Bike",
            "5637185849": "On the Rider",
            "5637185876": "Packaging",
            "5637185852": "Pads",
            "5637185847": "Rack",
            "5637185875": "Sticker",
            "5637185881": "Spareparts",
            "5637185854": "Stuffed Toy",
            "5637185883": "Toys",
            "5637185853": "Toy Accessories",
            "5637185886": "Used Bikes"
        },
        "category": "Product category",
        "clearRefinement": "Clear all filters",
        "colors": "colors",
        "height": "height",
        "latest": "Latest",
        "priceAscending": "Price ascending",
        "priceDescending": "Price descending",
        "rating": "Top rated",
        "sort": "Sort by {option}",
        "wheels": "wheels"
    },
    "en_US": {
        "age": "Kid's Age",
        "category": "Category",
        "height": "Kid's Height",
        "wheels": "Wheel Size"
    },
    "fi": {
        "age": "ikä",
        "categories": {
            "5637185826": "Kaikki",
            "5637185870": "Yleis",
            "5637185857": "Yleispyörät",
            "5637185850": "Vaatteet",
            "5637185836": "Laukku",
            "5637185837": "Kori",
            "5637185830": "Koritarvikkeet",
            "5637185838": "Soittokello",
            "5637185839": "Pyörälaukku",
            "5637185840": "Pyörävalot",
            "5637185841": "Pyöräpumput",
            "5637185874": "Kirjanen",
            "5637185843": "Pullo",
            "5637185862": "Sähköyleispyörät",
            "5637185864": "Sähkömaastopyörät",
            "5637185844": "Lokasuojat",
            "5637185851": "Käsineet",
            "5637185832": "Kypärä tarvikkeet",
            "5637185833": "Kypärävalo",
            "5637185845": "Seisontatuki",
            "5637185846": "Lukko",
            "5637185866": "Maastopyörät",
            "5637185873": "Sekalaista",
            "5637185835": "Pyörässä",
            "5637185849": "Pyöräilijällä",
            "5637185876": "Pakkaus",
            "5637185852": "Suojat",
            "5637185847": "Tavarateline",
            "5637185875": "Tarra",
            "5637185881": "Spareparts",
            "5637185854": "Pehmolelu",
            "5637185883": "Lelut",
            "5637185853": "Lelutarvikkeet",
            "5637185886": "Käytetyt pyörät"
        },
        "category": "Tuotekategoria",
        "clearRefinement": "Tyhjennä kaikki suodattimet",
        "colors": "värit",
        "height": "korkeus",
        "latest": "Uusimmat",
        "priceAscending": "Hinta nouseva",
        "priceDescending": "Hinta laskeva",
        "rating": "Parhaat arviot",
        "sort": "Lajittelu: {option}",
        "wheels": "kiekot"
    },
    "fr": {
        "age": "Âge",
        "categories": {
            "5637185826": "Tous les produits",
            "5637185870": "Polyvalent",
            "5637185857": "Vélos polyvalents",
            "5637185850": "Vêtements",
            "5637185836": "Sac",
            "5637185837": "Panier",
            "5637185830": "Accessoires panier",
            "5637185838": "Sonnette",
            "5637185839": "Sacs vélo",
            "5637185840": "Lampe de vélo",
            "5637185841": "Pompes à vélo",
            "5637185874": "Livret",
            "5637185843": "Gourde",
            "5637185862": "VAE polyvalents",
            "5637185864": "VTTAE",
            "5637185844": "Garde-boues",
            "5637185851": "Gants",
            "5637185832": "Accessoires casque",
            "5637185833": "Lampe de casque",
            "5637185845": "Béquille",
            "5637185846": "Antivol",
            "5637185866": "VTT",
            "5637185873": "Divers",
            "5637185835": "Pour le vélo",
            "5637185849": "Pour le ou la cycliste",
            "5637185876": "Emballage",
            "5637185852": "Protections et coussinets",
            "5637185847": "Support de vélo",
            "5637185875": "Autocollant",
            "5637185881": "Spareparts",
            "5637185854": "Peluche",
            "5637185883": "Jouets",
            "5637185853": "Accessoires de jeux",
            "5637185886": "Vélos d’occasion"
        },
        "category": "Catégorie de produits",
        "clearRefinement": "Supprimer tous les filtres",
        "colors": "Couleurs",
        "height": "Taille de l’enfant",
        "latest": "Plus récents",
        "priceAscending": "Prix croissants",
        "priceDescending": "Prix décroissants",
        "rating": "Mieux notés",
        "sort": "Trier par {option}",
        "wheels": "Taille du vélo"
    },
    "nl": {
        "age": "Leeftijd",
        "categories": {
            "5637185826": "Alles",
            "5637185870": "Allround",
            "5637185857": "Allroundfietsen",
            "5637185850": "Kleding",
            "5637185836": "Tas",
            "5637185837": "Mandje",
            "5637185830": "Accessoires voor mandjes",
            "5637185838": "Fietsbel",
            "5637185839": "Fietstassen",
            "5637185840": "Fietsverlichting",
            "5637185841": "Fietspompen",
            "5637185874": "Boekje",
            "5637185843": "Fles",
            "5637185862": "Allround-e-bikes",
            "5637185864": "E-mountainbikes",
            "5637185844": "Spatborden",
            "5637185851": "Handschoenen",
            "5637185832": "Helmaccessoires",
            "5637185833": "Helmlamp",
            "5637185845": "Fietsstandaard",
            "5637185846": "Fietsslot",
            "5637185866": "Mountainbikes",
            "5637185873": "Overig",
            "5637185835": "Voor de fiets",
            "5637185849": "Voor de fietser",
            "5637185876": "Verpakking",
            "5637185852": "Beschermers",
            "5637185847": "Bagagedrager",
            "5637185875": "Sticker",
            "5637185881": "Spareparts",
            "5637185854": "Knuffel",
            "5637185883": "Speelgoed",
            "5637185853": "Speelgoedaccessoires",
            "5637185886": "Gebruikte fietsen"
        },
        "category": "Productcategorie",
        "clearRefinement": "Alle filters wissen",
        "colors": "Kleuren",
        "height": "Lengte",
        "latest": "Nieuwste",
        "priceAscending": "Prijs oplopend",
        "priceDescending": "Prijs aflopend",
        "rating": "Best beoordeeld",
        "sort": "Sorteren op {option}",
        "wheels": "Maat"
    },
    "sv": {
        "age": "Ålder",
        "categories": {
            "5637185826": "Alla",
            "5637185870": "Allround",
            "5637185857": "Allroundcyklar",
            "5637185850": "Kläder",
            "5637185836": "Väska",
            "5637185837": "Korg",
            "5637185830": "Korgtillbehör",
            "5637185838": "Ringklocka",
            "5637185839": "Cykelväskor",
            "5637185840": "Cykelbelysning",
            "5637185841": "Cykelpumpar",
            "5637185874": "Häfte",
            "5637185843": "Flaska",
            "5637185862": "Eldrivna allroundcyklar",
            "5637185864": "Eldrivna mountainbikes",
            "5637185844": "Skärmar",
            "5637185851": "Handskar",
            "5637185832": "Hjälmtillbehör",
            "5637185833": "Hjälmlampa",
            "5637185845": "Cykelstöd",
            "5637185846": "Lås",
            "5637185866": "Mountainbikes",
            "5637185873": "Diverse",
            "5637185835": "På cykeln",
            "5637185849": "På cyklisten",
            "5637185876": "Förpackningen",
            "5637185852": "Skydd",
            "5637185847": "Pakethållare",
            "5637185875": "Klistermärke",
            "5637185881": "Spareparts",
            "5637185854": "Mjukisdjur",
            "5637185883": "Leksaker",
            "5637185853": "Leksakstillbehör",
            "5637185886": "Begagnade cyklar"
        },
        "category": "Produktkategori",
        "clearRefinement": "Ta bort alla filter",
        "colors": "Färger",
        "height": "Kroppslängd",
        "latest": "Nyast",
        "priceAscending": "Lägst pris",
        "priceDescending": "Högst pris",
        "rating": "Högst betyg",
        "sort": "Sortera efter {option}",
        "wheels": "Cykelstorlek"
    },
    "pl": {
        "age": "wiek",
        "categories": {
            "5637185826": "Wszystkie",
            "5637185870": "Uniwersalne",
            "5637185857": "Rowery uniwersalne",
            "5637185850": "Odzież",
            "5637185836": "Torba",
            "5637185837": "Koszyk",
            "5637185830": "Akcesoria do koszyka",
            "5637185838": "Dzwonek",
            "5637185839": "Torby rowerowe",
            "5637185840": "Lampka rowerowa",
            "5637185841": "Pompki rowerowe",
            "5637185874": "Książeczka",
            "5637185843": "Bidon",
            "5637185862": "E-rowery uniwersalne",
            "5637185864": "E-rowery górskie",
            "5637185844": "Błotniki",
            "5637185851": "Rękawiczki",
            "5637185832": "Akcesoria do kasku",
            "5637185833": "Lampka na kask",
            "5637185845": "Podpórka",
            "5637185846": "Zapięcie",
            "5637185866": "Rowery górskie",
            "5637185873": "Różne",
            "5637185835": "Na rower",
            "5637185849": "Dla rowerzysty",
            "5637185876": "Opakowanie",
            "5637185852": "Ochraniacze",
            "5637185847": "Bagażnik",
            "5637185875": "Naklejka",
            "5637185881": "Spareparts",
            "5637185854": "Pluszak",
            "5637185883": "Zabawki",
            "5637185853": "Akcesoria do zabawek",
            "5637185886": "Rowery używane"
        },
        "category": "Kategoria produktu",
        "clearRefinement": "Wyczyść wszystkie filtry",
        "colors": "kolory",
        "height": "wysokość",
        "latest": "Ostatnie",
        "priceAscending": "Cena rosnąco",
        "priceDescending": "Cena malejąco",
        "rating": "Najlepiej oceniane",
        "sort": "Sortuj według {option}",
        "wheels": "koła"
    },
    "es": {
        "age": "edad",
        "categories": {
            "5637185826": "Todos los productos",
            "5637185870": "All-round",
            "5637185857": "Bicicletas all-round",
            "5637185850": "Ropa",
            "5637185836": "Bolsas",
            "5637185837": "Cesta",
            "5637185830": "Accesorios para cestas",
            "5637185838": "Timbre",
            "5637185839": "Bolsas de transporte",
            "5637185840": "Luces para la bicicleta",
            "5637185841": "Bombas para bicicletas",
            "5637185874": "Folleto",
            "5637185843": "Botella",
            "5637185862": "Bicicletas all-round eléctricas",
            "5637185864": "Bicicletas de montaña eléctricas",
            "5637185844": "Guardabarros",
            "5637185851": "Guantes",
            "5637185832": "Accesorios para cascos",
            "5637185833": "Luces para cascos",
            "5637185845": "Pata de cabra",
            "5637185846": "Candado",
            "5637185866": "Bicicletas de montaña",
            "5637185873": "Varios",
            "5637185835": "Para la bicicleta",
            "5637185849": "Para el/la ciclista",
            "5637185876": "Paquetes",
            "5637185852": "Pastillas",
            "5637185847": "Portaequipajes",
            "5637185875": "Adhesivos",
            "5637185881": "Spareparts",
            "5637185854": "Peluche",
            "5637185883": "Juguetes",
            "5637185853": "Accesorios para juguetes",
            "5637185886": "Bicicletas de segunda mano"
        },
        "category": "Categoría de productos",
        "clearRefinement": "Borrar todos los filtros",
        "colors": "colores",
        "height": "altura",
        "latest": "Más recientes",
        "priceAscending": "Por precio ascendente",
        "priceDescending": "Por precio descendente",
        "rating": "Los más valorados",
        "sort": "Ordenar por {option}",
        "wheels": "ruedas"
    },
    "ro": {
        "age": "",
        "categories": {
            "5637185826": "",
            "5637185870": "",
            "5637185857": "",
            "5637185850": "",
            "5637185836": "",
            "5637185837": "",
            "5637185830": "",
            "5637185838": "",
            "5637185839": "",
            "5637185840": "",
            "5637185841": "",
            "5637185874": "",
            "5637185843": "",
            "5637185862": "",
            "5637185864": "",
            "5637185844": "",
            "5637185851": "",
            "5637185832": "",
            "5637185833": "",
            "5637185845": "",
            "5637185846": "",
            "5637185866": "",
            "5637185873": "",
            "5637185835": "",
            "5637185849": "",
            "5637185876": "",
            "5637185852": "",
            "5637185847": "",
            "5637185875": "",
            "5637185881": "",
            "5637185854": "",
            "5637185883": "",
            "5637185853": "",
            "5637185886": ""
        },
        "category": "",
        "clearRefinement": "",
        "colors": "",
        "height": "",
        "latest": "",
        "priceAscending": "",
        "priceDescending": "",
        "rating": "",
        "sort": "",
        "wheels": ""
    }
}
</i18n>
